import type { CatalogueTaxonomy } from '../types';

/**
 * Display labels for taxonomy slugs.
 *
 * The catalogue stores slugs (`upper-arms`, `body-weight`); the UI shows names
 * ("Upper Arms", "Body Weight"). That mapping lives here rather than in
 * components so there is exactly one copy of it, and so no component ever
 * parses `taxonomy.json` itself.
 *
 * `taxonomy.json` is emitted by the catalogue build and precached by the
 * service worker, so lookups work offline from first launch onward. It is the
 * same artefact the exercises came from — not a second data source.
 *
 * **Labels only.** Which body parts and equipment are actually *available* to
 * filter by comes from `exerciseRepository.facets()`, which reads the database,
 * so a user-created exercise's equipment appears in the filters even though the
 * catalogue's taxonomy has never heard of it.
 */

export const TAXONOMY_URL = '/catalogue/taxonomy.json';

export interface TaxonomyLabels {
  bodyPart(slug: string): string;
  muscle(slug: string): string;
  equipment(slug: string): string;
}

/**
 * Turn an unknown slug into something readable: `front-delts` → `Front Delts`.
 *
 * The fallback matters more than it looks. A missing label must never render as
 * a blank chip or a raw slug — a custom exercise (Phase 4B) can carry equipment
 * the bundled taxonomy has never seen, and that has to look deliberate.
 */
export function deslugify(slug: string): string {
  return slug
    .split(/[-_\s]+/)
    .filter((part) => part.length > 0)
    .map((part) => part[0]!.toUpperCase() + part.slice(1))
    .join(' ');
}

function toLookup(entries: { slug: string; name: string }[] | undefined): Map<string, string> {
  return new Map((entries ?? []).map((entry) => [entry.slug, entry.name]));
}

export interface TaxonomyRepository {
  /** Loads once, then serves from memory. Safe to call from many components. */
  labels(): Promise<TaxonomyLabels>;
  /** Test seam — drops the memoised result. */
  reset(): void;
}

export function createTaxonomyRepository(
  fetchImpl: () => typeof fetch = () => fetch,
): TaxonomyRepository {
  // Memoised as a promise, not a value: several components mount at once on
  // first render, and this must produce one request, not five.
  let inFlight: Promise<TaxonomyLabels> | null = null;

  async function load(): Promise<TaxonomyLabels> {
    let taxonomy: CatalogueTaxonomy | null = null;

    try {
      const response = await fetchImpl()(TAXONOMY_URL);
      if (response.ok) taxonomy = (await response.json()) as CatalogueTaxonomy;
    } catch {
      // Offline before the file was ever cached, or a malformed response.
      // Not fatal: `deslugify` still produces a readable label for every slug,
      // so the UI degrades to slightly-less-polished text rather than breaking.
      taxonomy = null;
    }

    const bodyParts = toLookup(taxonomy?.bodyParts);
    const muscles = toLookup(taxonomy?.muscles);
    const equipment = toLookup(taxonomy?.equipment);

    return {
      bodyPart: (slug) => bodyParts.get(slug) ?? deslugify(slug),
      muscle: (slug) => muscles.get(slug) ?? deslugify(slug),
      equipment: (slug) => equipment.get(slug) ?? deslugify(slug),
    };
  }

  return {
    async labels(): Promise<TaxonomyLabels> {
      inFlight ??= load();
      return await inFlight;
    },

    reset(): void {
      inFlight = null;
    },
  };
}

export const taxonomyRepository = createTaxonomyRepository();
